import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import {
  Box, Paper, Typography, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, CircularProgress,
  FormControl, Select, MenuItem, InputLabel, Button, Chip
} from '@mui/material';
import { RestartAltOutlined } from '@mui/icons-material';
import { updateScreenTitle } from '../../../LayoutContainers/AppLayout/redux/AppSlice';
import AgentService from '../../../services/AgentService';

const AgentUsage = () => {
  const dispatch = useDispatch();
  const [usage, setUsage] = useState([]);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);
  const [resetting, setResetting] = useState(null);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [dailyUsage, setDailyUsage] = useState([]);
  const [loadingDaily, setLoadingDaily] = useState(false);

  useEffect(() => {
    dispatch(updateScreenTitle('AI Agent Usage Analytics'));
    return () => dispatch(updateScreenTitle(''));
  }, [dispatch]);

  useEffect(() => {
    loadUsage();
  }, [days]);

  const loadUsage = async () => {
    setLoading(true);
    try {
      const data = await AgentService.getUsage(days);
      setUsage(data || []);
    } catch (error) {
      console.error('Failed to load usage:', error);
      setUsage([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectOrg = async (orgId, orgName) => {
    if (selectedOrg === orgId) {
      setSelectedOrg(null);
      setDailyUsage([]);
      return;
    }
    setSelectedOrg(orgId);
    setLoadingDaily(true);
    try {
      const data = await AgentService.getDailyUsage(orgId, days);
      setDailyUsage(Array.isArray(data) ? data : []);
    } catch (e) {
      setDailyUsage([]);
    } finally {
      setLoadingDaily(false);
    }
  };

  const handleResetUsage = async (orgId, orgName) => {
    if (!window.confirm(`Extend daily limit for ${orgName}? This adds +100K tokens and +50 conversations.`)) return;
    setResetting(orgId);
    try {
      await AgentService.resetUsage(orgId);
      alert(`Usage limit extended for ${orgName}`);
      loadUsage();
    } catch (error) {
      console.error('Failed to reset usage:', error);
    } finally {
      setResetting(null);
    }
  };

  const formatNumber = (num) => {
    if (!num) return '0';
    return num.toLocaleString();
  };

  const estimateCost = (inputTokens, outputTokens) => {
    // Claude Sonnet 4 pricing: $3/M input, $15/M output
    const cost = ((inputTokens || 0) / 1000000 * 3) + ((outputTokens || 0) / 1000000 * 15);
    return cost < 0.01 ? '< $0.01' : `$${cost.toFixed(2)}`;
  };

  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box>
            <Typography variant="h6">Usage Analytics</Typography>
            <Typography variant="body2" color="text.secondary">
              AI Agent usage across all organizations
            </Typography>
          </Box>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Period</InputLabel>
            <Select value={days} label="Period" onChange={(e) => setDays(e.target.value)}>
              <MenuItem value={7}>Last 7 days</MenuItem>
              <MenuItem value={30}>Last 30 days</MenuItem>
              <MenuItem value={90}>Last 90 days</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>Organization</strong></TableCell>
                  <TableCell align="right"><strong>Conversations</strong></TableCell>
                  <TableCell align="right"><strong>Input Tokens</strong></TableCell>
                  <TableCell align="right"><strong>Output Tokens</strong></TableCell>
                  <TableCell align="right"><strong>Est. Cost</strong></TableCell>
                  <TableCell align="center"><strong>Daily Usage</strong></TableCell>
                  <TableCell><strong>Last Used</strong></TableCell>
                  <TableCell align="center"><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {usage.map((row, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{ cursor: 'pointer', color: 'primary.main', fontWeight: selectedOrg === row.organizationId ? 600 : 400,
                              '&:hover': { textDecoration: 'underline' } }}
                        onClick={() => handleSelectOrg(row.organizationId, row.organizationName)}
                      >
                        {row.organizationName}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">{formatNumber(row.totalConversations)}</TableCell>
                    <TableCell align="right">{formatNumber(row.totalTokensInput)}</TableCell>
                    <TableCell align="right">{formatNumber(row.totalTokensOutput)}</TableCell>
                    <TableCell align="right">
                      {estimateCost(row.totalTokensInput, row.totalTokensOutput)}
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={`${row.usagePercent || 0}%`}
                        size="small"
                        color={
                          (row.usagePercent || 0) >= 100 ? 'error' :
                          (row.usagePercent || 0) >= 80 ? 'warning' : 'success'
                        }
                        sx={{ fontSize: '0.7rem', fontWeight: 600, minWidth: 55 }}
                      />
                    </TableCell>
                    <TableCell>
                      {row.lastUsed ? new Date(row.lastUsed).toLocaleDateString() : '-'}
                    </TableCell>
                    <TableCell align="center">
                      <Button
                        size="small"
                        variant="contained"
                        color="primary"
                        startIcon={resetting === row.organizationId ? <CircularProgress size={14} color="inherit" /> : <RestartAltOutlined />}
                        disabled={resetting === row.organizationId}
                        onClick={() => handleResetUsage(row.organizationId, row.organizationName)}
                        sx={{ fontSize: '0.7rem', textTransform: 'none' }}
                      >
                        Extend Limit
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {usage.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <Typography color="text.secondary" sx={{ py: 2 }}>
                        No usage data for this period
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Daily Usage Breakdown */}
      {selectedOrg && (
        <Paper sx={{ p: 3, mt: 2 }}>
          <Typography variant="h6" fontSize="0.95rem" fontWeight={600} gutterBottom>
            Daily Usage — {usage.find(u => u.organizationId === selectedOrg)?.organizationName || ''}
          </Typography>
          {loadingDaily ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem' }}>Date</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>Conversations</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>Input Tokens</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>Output Tokens</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>Total Tokens</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>Est. Cost</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {dailyUsage.map((row, index) => (
                    <TableRow key={index} hover>
                      <TableCell sx={{ fontSize: '0.8rem' }}>{row.date}</TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.8rem' }}>{formatNumber(row.conversations)}</TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.8rem' }}>{formatNumber(row.inputTokens)}</TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.8rem' }}>{formatNumber(row.outputTokens)}</TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.8rem', fontWeight: 600 }}>{formatNumber(row.totalTokens)}</TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.8rem' }}>
                        {estimateCost(row.inputTokens, row.outputTokens)}
                      </TableCell>
                    </TableRow>
                  ))}
                  {dailyUsage.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                        <Typography color="text.secondary" fontSize="0.8rem">No daily data available</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      )}
    </Box>
  );
};

export default AgentUsage;
